import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  Timestamp,
  onSnapshot,
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { auth } from "../firebase/auth";
import { db } from "../firebase/firestore";

type HistoryEntry = {
  field: string;
  oldValue: any;
  newValue: any;
  changedAt: Date;
  changedBy: string;
};

export default function Page3() {
  const currentUser = auth.currentUser;
  const phone = currentUser?.phoneNumber ?? null;

  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [allergyInput, setAllergyInput] = useState("");
  const [addingAllergy, setAddingAllergy] = useState(false);
  const [removingAllergy, setRemovingAllergy] = useState(false);

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  const prevNameRef = useRef<string>("");

  /* ---------- REALTIME DATA ---------- */
  useEffect(() => {
    if (!phone) {
      setLoading(false);
      return;
    }

    const ref = doc(db, "users", phone);

    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setUserData({ allergies: [], history: [], str: "Unnamed" });
        setLoading(false);
        prevNameRef.current = "Unnamed";
        return;
      }

      const data = snap.data();

      const normalizedHistory: HistoryEntry[] = (data.history || []).map(
        (h: any) => ({
          ...h,
          changedAt:
            h.changedAt instanceof Timestamp
              ? h.changedAt.toDate()
              : new Date(h.changedAt),
        })
      );

      setUserData({
        ...data,
        allergies: data.allergies || [],
        history: normalizedHistory,
      });

      if (!editingName) {
        setNameDraft(data.str || "");
        prevNameRef.current = data.str || "";
      }

      setLoading(false);

      if (savingName && data.str === nameDraft.trim()) {
        setSavingName(false);
        setEditingName(false);
        prevNameRef.current = data.str;
      }
    });

    return () => unsub();
  }, [phone, editingName, savingName, nameDraft]);

  const displayName = userData?.str || phone || "Unknown";

  const appendHistory = async (
    entry: Omit<HistoryEntry, "changedAt">
  ) => {
    if (!phone) return;

    await updateDoc(doc(db, "users", phone), {
      history: arrayUnion({
        ...entry,
        changedAt: serverTimestamp(),
      }),
    });
  };

  const handleAddAllergy = async () => {
    if (!phone || !allergyInput.trim()) return;

    setAddingAllergy(true);

    await updateDoc(doc(db, "users", phone), {
      allergies: arrayUnion(allergyInput.trim()),
      lastUpdated: serverTimestamp(),
    });

    await appendHistory({
      field: "allergy",
      oldValue: null,
      newValue: allergyInput.trim(),
      changedBy: displayName,
    });

    setAllergyInput("");
    setAddingAllergy(false);
  };

  const handleDeleteAllergy = async (allergy: string) => {
    if (!phone) return;

    setRemovingAllergy(true);

    await updateDoc(doc(db, "users", phone), {
      allergies: arrayRemove(allergy),
      lastUpdated: serverTimestamp(),
    });

    await appendHistory({
      field: "allergy_removed",
      oldValue: allergy,
      newValue: null,
      changedBy: displayName,
    });

    setRemovingAllergy(false);
  };

  const saveName = async () => {
    if (!phone || savingName) return;

    const newName = nameDraft.trim();
    const oldName = prevNameRef.current;

    if (newName === oldName) {
      setEditingName(false);
      return;
    }

    setSavingName(true);

    await updateDoc(doc(db, "users", phone), {
      str: newName,
      lastUpdated: serverTimestamp(),
    });

    await appendHistory({
      field: "name",
      oldValue: oldName,
      newValue: newName,
      changedBy: newName,
    });
  };

  if (!phone) return <div>Not authenticated</div>;
  if (loading) return <div>Loading…</div>;

  return (
    <div style={{ padding: "1.5rem", maxWidth: 600 }}>
      <h2>Welcome, {displayName}</h2>

      {/* NAME EDIT */}
      <section style={{ marginBottom: "1rem" }}>
        {editingName ? (
          <>
            <input
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              disabled={savingName}
            />
            <button onClick={saveName} disabled={savingName}>
              {savingName ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setEditingName(false)} disabled={savingName}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <strong>{userData?.str || "Unnamed"}</strong>{" "}
            <button onClick={() => setEditingName(true)}>Edit</button>
          </>
        )}
      </section>

      {/* ALLERGIES */}
      <section style={{ marginBottom: "1rem" }}>
        <h3>Allergies</h3>
        {userData.allergies.length === 0 ? (
          <p>No allergies listed.</p>
        ) : (
          <ul>
            {userData.allergies.map((a: string) => (
              <li key={a}>
                {a}{" "}
                <button
                  onClick={() => handleDeleteAllergy(a)}
                  disabled={removingAllergy}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}

        <input
          placeholder="Add allergy"
          value={allergyInput}
          onChange={(e) => setAllergyInput(e.target.value)}
          disabled={addingAllergy}
        />
        <button
          onClick={handleAddAllergy}
          disabled={addingAllergy || !allergyInput.trim()}
        >
          {addingAllergy ? "Adding…" : "Add"}
        </button>
      </section>

      {/* HISTORY */}
      <section>
        <h3>Change History</h3>
        {userData.history.length === 0 ? (
          <p>No changes yet.</p>
        ) : (
          <ul>
            {userData.history
              .slice()
              .reverse()
              .map((h: any, idx: number) => (
                <li key={idx}>
                  [{h.changedAt?.toLocaleString()}] {h.field}:{" "}
                  {h.oldValue ?? "-"} → {h.newValue ?? "-"} by {h.changedBy}
                </li>
              ))}
          </ul>
        )}
      </section>
    </div>
  );
}